import type { NavigateFunction } from "react-router-dom";

export const FORM_HANDLER_LAUNCH_STORAGE_KEY = "portalFormHandlerLaunch";

/** Passed via React Router `location.state` and mirrored in `sessionStorage` for reload. */
export type FormHandlerLaunchState = {
  facilityFormId: number;
  taskId: string;
  formTitleHint: string;
  patientLabel: string;
  formCountHint: number;
};

export function isValidFormHandlerLaunchState(o: unknown): o is FormHandlerLaunchState {
  if (!o || typeof o !== "object") return false;
  const s = o as Record<string, unknown>;
  const facilityFormId = Number(s.facilityFormId);
  const taskId = String(s.taskId ?? "").trim();
  if (!Number.isFinite(facilityFormId) || facilityFormId <= 0) return false;
  if (!taskId) return false;
  return true;
}

/** Normalize optional string/number fields after structural validation. */
export function normalizeFormHandlerLaunchState(o: unknown): FormHandlerLaunchState | null {
  if (!isValidFormHandlerLaunchState(o)) return null;
  const s = o as Record<string, unknown>;
  return {
    facilityFormId: Number(s.facilityFormId),
    taskId: String(s.taskId ?? "").trim(),
    formTitleHint: String(s.formTitleHint ?? ""),
    patientLabel: String(s.patientLabel ?? ""),
    formCountHint: Math.max(1, Number(s.formCountHint) || 1),
  };
}

export function writeFormHandlerLaunch(state: FormHandlerLaunchState): void {
  try {
    sessionStorage.setItem(FORM_HANDLER_LAUNCH_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* quota / private mode */
  }
}

export function readFormHandlerLaunch(): FormHandlerLaunchState | null {
  try {
    const raw = sessionStorage.getItem(FORM_HANDLER_LAUNCH_STORAGE_KEY);
    if (!raw) return null;
    return normalizeFormHandlerLaunchState(JSON.parse(raw) as unknown);
  } catch {
    return null;
  }
}

export function clearFormHandlerLaunch(): void {
  try {
    sessionStorage.removeItem(FORM_HANDLER_LAUNCH_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function navigateToFormHandler(
  navigate: NavigateFunction,
  state: FormHandlerLaunchState,
  options?: { replace?: boolean }
): void {
  writeFormHandlerLaunch(state);
  navigate("/form-handler", { state, replace: options?.replace });
}

/** One-time migration from legacy query URLs (`?i=&tid=` …). */
export function formHandlerLaunchFromLegacySearchParams(
  searchParams: URLSearchParams
): FormHandlerLaunchState | null {
  const facilityFormId = Number(searchParams.get("i"));
  const taskId = (searchParams.get("tid") ?? "").trim();
  if (!Number.isFinite(facilityFormId) || facilityFormId <= 0) return null;
  if (!taskId) return null;
  return {
    facilityFormId,
    taskId,
    formTitleHint: searchParams.get("t") ?? "",
    patientLabel: searchParams.get("p") ?? "",
    formCountHint: Math.max(1, Number(searchParams.get("fc") || "1") || 1),
  };
}
